import { toast } from "sonner";

import { apiErrorCode, apiErrorParams, apiErrorStatus } from "@/api/client";
import i18n from "@/lib/i18n";

/** Declarative toast config for React Query queries and mutations (via `meta`). */
export type ApiErrorMeta = {
  /** i18n key shown for any failure; `false` disables auto toast. */
  errorToast?: string | false;
  errorToastByStatus?: Partial<Record<number, string>>;
  /** Override global `errors.{code}` mapping for this query/mutation. */
  errorToastByCode?: Record<string, string>;
};

export const apiErrorMeta = {
  projectServiceFrontsLoad: { errorToast: "projectServiceFronts.loadError" },
  serviceFrontModelsLoad: { errorToast: "serviceFrontModels.loadError" },
  checklistModelsLoad: { errorToast: "checklistModels.loadError" },
  projectChecklistsLoad: { errorToast: "projectDiaryEntries.checklistsLoadError" },
  organizationLoad: { errorToast: "settings.loadError" },
  projectServiceFrontWrite: { errorToast: "settings.saveError" },
  serviceFrontModelWrite: { errorToast: "settings.saveError" },
  serviceFrontModelDelete: { errorToast: "settings.saveError" },
  checklistModelWrite: { errorToast: "settings.saveError" },
  checklistModelDelete: { errorToast: "settings.saveError" },
  projectServiceFrontDelete: { errorToast: "settings.saveError" },
  workersLoad: { errorToast: "workers.loadError" },
  workerWrite: { errorToast: "settings.saveError" },
  workerDelete: { errorToast: "settings.saveError" },
  workerRolesLoad: { errorToast: "workerRoles.loadError" },
  workerRoleWrite: { errorToast: "settings.saveError" },
  workerRoleDelete: {
    errorToast: "settings.saveError",
    errorToastByCode: { WORKER_ROLE_IN_USE: "workerRoles.deleteInUse" },
  },
  occurrencesLoad: { errorToast: "occurrences.loadError" },
  occurrenceWrite: { errorToast: "settings.saveError" },
  occurrenceDelete: { errorToast: "settings.saveError" },
  documentCategoriesLoad: { errorToast: "documentCategories.loadError" },
  documentCategoryWrite: { errorToast: "settings.saveError" },
  documentCategoryDelete: { errorToast: "settings.saveError" },
  projectDocumentsLoad: { errorToast: "projectDocuments.loadError" },
  projectDocumentWrite: { errorToast: "projectDocuments.saveError" },
  projectDocumentDelete: { errorToast: "projectDocuments.deleteError" },
  projectBudgetsLoad: { errorToast: "projectBudgets.loadError" },
  projectBudgetWrite: {
    errorToast: "projectBudgets.saveError",
    errorToastByCode: {
      PROJECT_BUDGET_NOT_EDITABLE: "errors.PROJECT_BUDGET_NOT_EDITABLE",
      PROJECT_BUDGET_FRONT_HAS_EDITABLE: "errors.PROJECT_BUDGET_FRONT_HAS_EDITABLE",
    },
  },
  projectBudgetDelete: {
    errorToast: "projectBudgets.deleteError",
    errorToastByCode: { PROJECT_BUDGET_IN_USE: "projectBudgets.deleteInUse" },
  },
  projectBudgetStatusesLoad: { errorToast: "projectBudgetStatuses.loadError" },
  projectClientBudgetsLoad: { errorToast: "projectClientBudgets.loadError" },
  projectClientBudgetWrite: {
    errorToast: "projectClientBudgets.saveError",
    errorToastByCode: {
      CLIENT_BUDGET_NOT_EDITABLE: "errors.CLIENT_BUDGET_NOT_EDITABLE",
      CLIENT_BUDGET_DISCOUNT_EXCEEDS_TOTAL: "errors.CLIENT_BUDGET_DISCOUNT_EXCEEDS_TOTAL",
      INVALID_CLIENT_BUDGET_SERVICE_FRONT_IDS: "errors.INVALID_CLIENT_BUDGET_SERVICE_FRONT_IDS",
      INVALID_CLIENT_BUDGET_MODEL_STEP_IDS: "errors.INVALID_CLIENT_BUDGET_MODEL_STEP_IDS",
      CLIENT_BUDGET_ALREADY_HAS_PROJECT: "errors.CLIENT_BUDGET_ALREADY_HAS_PROJECT",
      CLIENT_BUDGET_NOT_APPROVED: "errors.CLIENT_BUDGET_NOT_APPROVED",
      CLIENT_BUDGET_PROPOSED_NAME_REQUIRED: "errors.CLIENT_BUDGET_PROPOSED_NAME_REQUIRED",
    },
  },
  projectClientBudgetDelete: {
    errorToast: "projectClientBudgets.deleteError",
    errorToastByCode: { CLIENT_BUDGET_IN_USE: "projectClientBudgets.deleteInUse" },
  },
  projectClientBudgetStatusesLoad: { errorToast: "projectClientBudgetStatuses.loadError" },
  projectClientBudgetStatusWrite: { errorToast: "settings.saveError" },
  projectTrackingLoad: { errorToast: "projectTracking.loadError" },
  projectBudgetStatusWrite: { errorToast: "settings.saveError" },
  materialCategoriesLoad: { errorToast: "materialCategories.loadError" },
  materialCategoryWrite: { errorToast: "settings.saveError" },
  materialCategoryDelete: { errorToast: "settings.saveError" },
  measurementUnitsLoad: { errorToast: "measurementUnits.loadError" },
  measurementUnitWrite: { errorToast: "settings.saveError" },
  measurementUnitDelete: {
    errorToast: "settings.saveError",
    errorToastByCode: { MEASUREMENT_UNIT_IN_USE: "measurementUnits.deleteInUse" },
  },
  materialsLoad: { errorToast: "materials.loadError" },
  materialWrite: { errorToast: "settings.saveError" },
  materialStockAdjust: {
    errorToast: "settings.saveError",
    errorToastByCode: { MATERIAL_INSUFFICIENT_STOCK: "materials.stockInsufficient" },
  },
  materialDelete: { errorToast: "settings.saveError" },
  equipmentCategoriesLoad: { errorToast: "equipmentCategories.loadError" },
  equipmentCategoryWrite: { errorToast: "settings.saveError" },
  equipmentCategoryDelete: { errorToast: "settings.saveError" },
  equipmentsLoad: { errorToast: "equipments.loadError" },
  equipmentWrite: { errorToast: "settings.saveError" },
  equipmentStockAdjust: {
    errorToast: "settings.saveError",
    errorToastByCode: { EQUIPMENT_INSUFFICIENT_QUANTITY: "equipments.stockInsufficient" },
  },
  equipmentDelete: { errorToast: "settings.saveError" },
  equipmentStatusChange: { errorToast: "settings.saveError" },
  equipmentStockMovementsLoad: { errorToast: "equipmentStockMovements.loadError" },
  weatherConditionsLoad: { errorToast: "weatherConditions.loadError" },
  projectPrioritiesLoad: { errorToast: "projectPriorities.loadError" },
  projectPriorityWrite: { errorToast: "settings.saveError" },
  projectPriorityDelete: {
    errorToast: "settings.saveError",
    errorToastByCode: { PROJECT_PRIORITY_IN_USE: "projectPriorities.deleteInUse" },
  },
  projectTypesLoad: { errorToast: "projectTypes.loadError" },
  projectTypeWrite: { errorToast: "settings.saveError" },
  projectTypeDelete: {
    errorToast: "settings.saveError",
    errorToastByCode: { PROJECT_TYPE_IN_USE: "projectTypes.deleteInUse" },
  },
  projectStatusesLoad: { errorToast: "projectStatuses.loadError" },
  projectStatusWrite: { errorToast: "settings.saveError" },
  serviceFrontDeadlineStatusesLoad: { errorToast: "serviceFrontDeadlineStatuses.loadError" },
  serviceFrontDeadlineStatusWrite: { errorToast: "settings.saveError" },
  projectsLoad: { errorToast: "projects.loadError" },
  projectDetail: { errorToast: false },
  projectWrite: { errorToast: "settings.saveError" },
  projectDelete: { errorToast: "settings.saveError" },
  projectActive: {
    errorToast: "settings.saveError",
    errorToastByCode: { PROJECT_INACTIVE: "errors.PROJECT_INACTIVE" },
  },
  organizationPatch: { errorToast: "settings.saveError" },
  projectDiaryEntriesLoad: { errorToast: "projectDiaryEntries.loadError" },
  projectDiaryEntryWrite: {
    errorToast: "settings.saveError",
    errorToastByCode: {
      PROJECT_MATERIAL_INSUFFICIENT_QUANTITY: "projectDiaryEntries.materialQuantityExceedsProject",
    },
  },
  projectDiaryEntryDelete: { errorToast: "settings.saveError" },
  projectOccurrencesLoad: { errorToast: "projectOccurrences.loadError" },
  projectPhotosLoad: { errorToast: "projectPhotos.loadError" },
  projectPhotoWrite: { errorToast: "projectPhotos.saveError" },
  projectPhotoDelete: { errorToast: "projectPhotos.deleteError" },
  photoTagsLoad: { errorToast: "photoTags.loadError" },
  photoTagWrite: { errorToast: "settings.saveError" },
  photoTagDelete: { errorToast: "settings.saveError" },
  projectMaterialsLoad: { errorToast: "projectMaterials.loadError" },
  projectMaterialSelectionLoad: { errorToast: "projectMaterialSelection.loadError" },
  projectMaterialSelectionWrite: { errorToast: "projectMaterialSelection.saveError" },
  projectMaterialSelectionDelete: { errorToast: "projectMaterialSelection.deleteError" },
  projectMaterialMovementsLoad: { errorToast: "projectMaterialMovements.loadError" },
  materialStockMovementsLoad: { errorToast: "materialStockMovements.loadError" },
  projectEquipmentsLoad: { errorToast: "projectEquipments.loadError" },
  projectEquipmentSelectionLoad: { errorToast: "projectEquipmentSelection.loadError" },
  projectEquipmentSelectionWrite: { errorToast: "projectEquipmentSelection.saveError" },
  projectEquipmentSelectionDelete: { errorToast: "projectEquipmentSelection.deleteError" },
  projectEquipmentMovementsLoad: { errorToast: "projectEquipmentMovements.loadError" },
  dashboardLoad: { errorToast: "dashboard.loadError" },
  partnersLoad: { errorToast: "partners.loadError" },
  partnerWrite: {
    errorToast: "settings.saveError",
    errorToastByCode: {
      PARTNER_NAME_TAKEN: "partners.nameTaken",
      PARTNER_DOCUMENT_TAKEN: "partners.documentTaken",
      PARTNER_CODE_TAKEN: "partners.codeTaken",
    },
  },
  partnerDelete: {
    errorToast: "settings.saveError",
    errorToastByCode: { PARTNER_IN_USE: "partners.deleteInUse" },
  },
  financialCategoriesLoad: { errorToast: "financialCategories.loadError" },
  financialCategoryWrite: {
    errorToast: "settings.saveError",
    errorToastByCode: { FINANCIAL_CATEGORY_NAME_TAKEN: "financialCategories.nameTaken" },
  },
  financialCategoryDelete: {
    errorToast: "settings.saveError",
    errorToastByCode: { FINANCIAL_CATEGORY_IN_USE: "financialCategories.deleteInUse" },
  },
  paymentMethodsLoad: { errorToast: "paymentMethods.loadError" },
  paymentMethodWrite: {
    errorToast: "settings.saveError",
    errorToastByCode: { PAYMENT_METHOD_NAME_TAKEN: "paymentMethods.nameTaken" },
  },
  paymentMethodDelete: {
    errorToast: "settings.saveError",
    errorToastByCode: { PAYMENT_METHOD_IN_USE: "paymentMethods.deleteInUse" },
  },
  projectPayablesLoad: { errorToast: "payables.loadError" },
  projectPayableWrite: { errorToast: "payables.saveError" },
  projectPayableDelete: { errorToast: "payables.deleteError" },
  projectReceivablesLoad: { errorToast: "receivables.loadError" },
  projectReceivableWrite: { errorToast: "receivables.saveError" },
  projectReceivableDelete: { errorToast: "receivables.deleteError" },
  chargeAccountLoad: { errorToast: false },
  chargeAccountWrite: {
    errorToast: "chargeAccount.saveError",
    errorToastByCode: {
      PAGARME_NOT_CONFIGURED: "errors.PAGARME_NOT_CONFIGURED",
      PAGARME_NOT_AVAILABLE_FOR_COUNTRY: "errors.PAGARME_NOT_AVAILABLE_FOR_COUNTRY",
      PAGARME_ORG_PROFILE_INCOMPLETE: "errors.PAGARME_ORG_PROFILE_INCOMPLETE",
      PAGARME_REGISTRATION_NOT_ALLOWED: "errors.PAGARME_REGISTRATION_NOT_ALLOWED",
      PAGARME_REPRESENTATIVE_DOCUMENT_INVALID: "errors.PAGARME_REPRESENTATIVE_DOCUMENT_INVALID",
      PAGARME_REQUEST_FAILED: "errors.PAGARME_REQUEST_FAILED",
    },
  },
  asaasAccountLoad: { errorToast: false },
  asaasChargeLoad: { errorToast: false },
  asaasOnboard: {
    errorToast: "asaas.onboardError",
    errorToastByCode: {
      ASAAS_NOT_CONFIGURED: "errors.ASAAS_NOT_CONFIGURED",
      ASAAS_NOT_AVAILABLE_FOR_COUNTRY: "errors.ASAAS_NOT_AVAILABLE_FOR_COUNTRY",
      ASAAS_ORG_PROFILE_INCOMPLETE: "errors.ASAAS_ORG_PROFILE_INCOMPLETE",
      ASAAS_REQUEST_FAILED: "errors.ASAAS_REQUEST_FAILED",
    },
  },
  asaasChargeWrite: {
    errorToast: "receivables.chargeCreateError",
    errorToastByCode: {
      ASAAS_ACCOUNT_NOT_READY: "errors.ASAAS_ACCOUNT_NOT_READY",
      ASAAS_CHARGE_NOT_ALLOWED: "errors.ASAAS_CHARGE_NOT_ALLOWED",
      ASAAS_PARTNER_DOCUMENT_INVALID: "errors.ASAAS_PARTNER_DOCUMENT_INVALID",
      ASAAS_PARTNER_INCOMPLETE: "errors.ASAAS_PARTNER_INCOMPLETE",
      ASAAS_CHARGE_ALREADY_EXISTS: "errors.ASAAS_CHARGE_ALREADY_EXISTS",
      ASAAS_REQUEST_FAILED: "errors.ASAAS_REQUEST_FAILED",
      PAGARME_ACCOUNT_NOT_READY: "errors.PAGARME_ACCOUNT_NOT_READY",
      PAGARME_CHARGE_NOT_ALLOWED: "errors.PAGARME_CHARGE_NOT_ALLOWED",
      PAGARME_PARTNER_DOCUMENT_INVALID: "errors.PAGARME_PARTNER_DOCUMENT_INVALID",
      PAGARME_PARTNER_INCOMPLETE: "errors.PAGARME_PARTNER_INCOMPLETE",
      PAGARME_CHARGE_ALREADY_EXISTS: "errors.PAGARME_CHARGE_ALREADY_EXISTS",
      PAGARME_CHARGE_NOT_CANCELLABLE: "errors.PAGARME_CHARGE_NOT_CANCELLABLE",
      PAGARME_REQUEST_FAILED: "errors.PAGARME_REQUEST_FAILED",
    },
  },
  cashFlowLoad: { errorToast: "cashFlow.loadError" },
  projectFinanceLoad: { errorToast: "projectCosts.loadError" },
  quantityAdvanceWrite: { errorToast: "projectCosts.advanceSaveError" },
  purchasingLoad: { errorToast: "purchaseRequests.loadError" },
  purchasingWrite: { errorToast: "purchaseRequests.saveError" },
  fiscalLoad: {
    errorToast: "fiscal.loadError",
    errorToastByCode: { FISCAL_DISABLED: "errors.FISCAL_DISABLED" },
  },
  fiscalWrite: {
    errorToast: "fiscal.saveError",
    errorToastByCode: {
      SERVICE_TAX_CODE_IN_USE: "fiscal.taxCodeDeleteInUse",
      FISCAL_NOT_READY: "fiscal.notReady",
      FISCAL_DOCUMENT_ALREADY_EMITTED: "fiscal.alreadyEmitted",
      FISCAL_DOCUMENT_NOT_CANCELLABLE: "fiscal.notCancellable",
      FISCAL_XML_INVALID: "fiscal.xmlInvalid",
      FISCAL_CERTIFICATE_INVALID: "errors.FISCAL_CERTIFICATE_INVALID",
      FISCAL_CERTIFICATE_PASSWORD: "errors.FISCAL_CERTIFICATE_PASSWORD",
      FISCAL_CERTIFICATE_CNPJ_MISMATCH: "errors.FISCAL_CERTIFICATE_CNPJ_MISMATCH",
      FISCAL_CERTIFICATE_EXPIRED: "errors.FISCAL_CERTIFICATE_EXPIRED",
      FISCAL_CERTIFICATE_NOT_ICP_BRASIL: "errors.FISCAL_CERTIFICATE_NOT_ICP_BRASIL",
      FISCAL_CERTIFICATE_MISSING: "errors.FISCAL_CERTIFICATE_MISSING",
      FISCAL_ORG_CNPJ_REQUIRED: "errors.FISCAL_ORG_CNPJ_REQUIRED",
      FISCAL_CERTIFICATE_VAULT_UNAVAILABLE: "errors.FISCAL_CERTIFICATE_VAULT_UNAVAILABLE",
      FISCAL_SEFIN_UNAVAILABLE: "errors.FISCAL_SEFIN_UNAVAILABLE",
      FISCAL_DISABLED: "errors.FISCAL_DISABLED",
    },
  },
} as const satisfies Record<string, ApiErrorMeta>;

function withFriendlyApiErrorParams(
  code: string,
  params?: Record<string, string | number | boolean>,
): Record<string, string | number | boolean> | undefined {
  if (!params) return params;
  if (code !== "PLAN_LIMIT_REACHED") return params;
  const resource = params.resource;
  if (typeof resource !== "string") return params;
  const resourceKey = `errors.planResources.${resource}`;
  if (!i18n.exists(resourceKey)) return params;
  return { ...params, resource: i18n.t(resourceKey) };
}

export function resolveApiErrorMessage(
  code: string,
  params?: Record<string, string | number | boolean>,
): string {
  if (code === "ASAAS_REQUEST_FAILED") {
    const reason = params?.reason;
    if (typeof reason === "string" && reason.trim()) return reason.trim();
  }
  const key = `errors.${code}`;
  if (i18n.exists(key)) {
    return i18n.t(key, withFriendlyApiErrorParams(code, params));
  }
  return i18n.t("errors.unexpected");
}

export function resolveApiErrorFromException(err: unknown): string {
  const code = apiErrorCode(err);
  if (!code) return i18n.t("errors.unexpected");
  return resolveApiErrorMessage(code, apiErrorParams(err));
}

export function resolveApiErrorToast(err: unknown, meta?: ApiErrorMeta): string | null {
  if (meta?.errorToast === false) return null;

  const code = apiErrorCode(err);
  const params = apiErrorParams(err);

  if (code && meta?.errorToastByCode?.[code]) {
    const toastKey = meta.errorToastByCode[code];
    if (toastKey === `errors.${code}`) {
      return resolveApiErrorMessage(code, params);
    }
    return i18n.t(toastKey);
  }

  if (code && i18n.exists(`errors.${code}`)) {
    return resolveApiErrorMessage(code, params);
  }

  const status = apiErrorStatus(err);
  if (status != null && meta?.errorToastByStatus?.[status] != null) {
    return i18n.t(meta.errorToastByStatus[status]!);
  }

  if (typeof meta?.errorToast === "string") {
    return i18n.t(meta.errorToast);
  }

  if (status === undefined || status >= 500) {
    return i18n.t("errors.unexpected");
  }

  return null;
}

export function showApiErrorToast(err: unknown, meta?: ApiErrorMeta): void {
  const message = resolveApiErrorToast(err, meta);
  if (message) toast.error(message);
}
